/**
 * RedMart product details: "Show more" toggle for the specifications list.
 * Rows past the configured visible count render with [hidden data-rm-spec-extra];
 * this just flips that attribute and swaps the button label/state.
 */

class RmSpecsToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-rm-specs-more]');
    this.list = this.previousElementSibling;
    if (!this.button || !this.list) return;

    this.moreLabel = this.dataset.moreLabel || this.button.textContent;
    this.lessLabel = this.dataset.lessLabel || this.moreLabel;

    this.button.addEventListener('click', () => this.#toggle());
  }

  #toggle() {
    const expanded = this.button.getAttribute('aria-expanded') === 'true';
    for (const row of this.list.querySelectorAll('[data-rm-spec-extra]')) row.hidden = expanded;
    this.button.setAttribute('aria-expanded', String(!expanded));
    this.button.textContent = expanded ? this.moreLabel : this.lessLabel;
  }
}

if (!customElements.get('rm-specs-toggle')) customElements.define('rm-specs-toggle', RmSpecsToggle);

/**
 * Sticky in-page nav: highlights the tab for the section being read and slides
 * a single underline between tabs. Clicking a tab scrolls there smoothly; while
 * that scroll runs, the spy is paused so the underline goes straight to the
 * chosen tab instead of stepping through every tab in between.
 *
 * From 990px the theme scrolls .page-wrapper, not the window (base.css), so
 * scroll events are caught on document in the capture phase and the scroller
 * is looked up rather than assumed.
 */
class RmSectionNav extends HTMLElement {
  #links = [];
  #bar = null;
  #active = null;
  #lockedUntilIdle = false;
  #idleTimer = 0;
  #frame = 0;

  connectedCallback() {
    this.#bar = this.querySelector('.rm-pdetails__nav-bar');
    this.#links = [...this.querySelectorAll('a[href^="#"]')]
      .map((link) => ({ link, target: document.getElementById(link.hash.slice(1)) }))
      .filter((item) => item.target);
    if (!this.#links.length) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onScroll = () => this.#schedule();
    this.onResize = () => this.#moveBar(this.#active, false);

    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.onResize);
    for (const item of this.#links) {
      item.link.addEventListener('click', (event) => this.#go(event, item));
    }

    this.#setActive(this.#current(), false);
    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onResize);
  }

  /** Height covered by the sticky header and this nav, plus breathing room. */
  #offset() {
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--rm-sticky-header-height')) || 0;
    return header + this.closest('nav').offsetHeight + 24;
  }

  /**
   * The section whose top has most recently passed under the nav. Tabs are not
   * in page order (FAQs sit above "Complete the set" and Reviews), so this
   * compares positions rather than walking the tabs in order.
   */
  #scroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY)) return wrapper;
    return document.scrollingElement;
  }

  #current() {
    // A section counts as "being read" once its top is 30% of the way down the
    // visible area below the sticky bars, not only when it hits the very top.
    const offset = this.#offset();
    const line = offset + (window.innerHeight - offset) * 0.3;
    const tops = this.#links.map((item) => ({ item, top: item.target.getBoundingClientRect().top - line }));
    const scroller = this.#scroller();
    const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
    if (atBottom) return tops.reduce((a, b) => (b.top > a.top ? b : a)).item;
    const passed = tops.filter((t) => t.top <= 1);
    if (!passed.length) return tops.reduce((a, b) => (b.top < a.top ? b : a)).item;
    return passed.reduce((a, b) => (b.top > a.top ? b : a)).item;
  }

  #schedule() {
    if (this.#lockedUntilIdle) {
      clearTimeout(this.#idleTimer);
      this.#idleTimer = setTimeout(() => (this.#lockedUntilIdle = false), 150);
      return;
    }
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#setActive(this.#current(), true));
  }

  #go(event, item) {
    event.preventDefault();
    const smooth = !this.reducedMotion.matches;
    this.#lockedUntilIdle = smooth;
    this.#setActive(item, true);
    // scrollIntoView finds the right scroller; the margin keeps the heading clear of the sticky bars.
    item.target.style.scrollMarginTop = `${this.#offset() - 1}px`;
    item.target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    history.replaceState(null, '', item.link.hash);
  }

  #setActive(item, animate) {
    if (!item || item === this.#active) return;
    this.#active?.link.removeAttribute('aria-current');
    item.link.setAttribute('aria-current', 'true');
    this.#active = item;
    this.#moveBar(item, animate);

    // Keep the active tab in view when the tab row scrolls sideways.
    const row = this;
    const left = item.link.offsetLeft - row.clientWidth / 2 + item.link.offsetWidth / 2;
    row.scrollTo({ left, behavior: animate && !this.reducedMotion.matches ? 'smooth' : 'auto' });
  }

  #moveBar(item, animate) {
    if (!item || !this.#bar) return;
    this.#bar.classList.toggle('is-instant', !animate);
    this.#bar.style.width = `${item.link.offsetWidth}px`;
    this.#bar.style.transform = `translateX(${item.link.offsetLeft}px)`;
  }
}

if (!customElements.get('rm-section-nav')) customElements.define('rm-section-nav', RmSectionNav);
